import { useQueryClient } from '@tanstack/react-query';
import {
  getShippingRulesMode,
  listShippingRules,
  showShippingRule,
  createShippingRule,
  updateShippingRule,
  toggleShippingRule,
  deleteShippingRule,
  evaluateShippingRules,
} from '@/api';
import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import type {
  ShippingRule,
  ShippingRuleCreatePayload,
  ShippingRuleUpdatePayload,
  ShippingRulesListResponse,
  ShippingRuleResponse,
  ShippingRuleModeResponse,
  EvaluateShippingRulesRequest,
  EvaluateShippingRulesResponse,
  ErrorMessageResponse,
} from '@/types';

const QUERY_KEYS = {
  list: ['shipping-rules'] as const,
  mode: ['shipping-rules-mode'] as const,
  show: (uid: string) => ['shipping-rule', uid] as const,
};

/** GET /v1/merchants/shipping-rules/mode — returns 'A' or 'B'. */
export const useGetShippingRulesMode = (enabled = true) =>
  useSentryQuery<ShippingRuleModeResponse, Error>(
    QUERY_KEYS.mode,
    () => getShippingRulesMode(),
    { enabled, staleTime: 5 * 60 * 1000 }, // 5 min — mode rarely flips
  );

/** GET /v1/merchants/shipping-rules — list owned rules. */
export const useListShippingRules = (enabled = true) =>
  useSentryQuery<ShippingRulesListResponse, Error>(
    QUERY_KEYS.list,
    () => listShippingRules(),
    { enabled },
  );

/** GET /v1/merchants/shipping-rules/{uid}. */
export const useShowShippingRule = (uid: string, enabled = true) =>
  useSentryQuery<ShippingRuleResponse, Error>(
    QUERY_KEYS.show(uid),
    () => showShippingRule(uid),
    { enabled: enabled && !!uid },
  );

/** POST — invalidates the list on success. */
export const useCreateShippingRule = () => {
  const qc = useQueryClient();
  return useSentryMutation<ShippingRuleResponse, ErrorMessageResponse, ShippingRuleCreatePayload>(
    (payload) => createShippingRule(payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(QUERY_KEYS.list);
        qc.invalidateQueries(QUERY_KEYS.mode);
      },
    },
  );
};

/** PATCH — invalidates the list and the specific rule. */
export const useUpdateShippingRule = (uid: string) => {
  const qc = useQueryClient();
  return useSentryMutation<ShippingRuleResponse, ErrorMessageResponse, ShippingRuleUpdatePayload>(
    (payload) => updateShippingRule(uid, payload),
    {
      onSuccess: () => {
        qc.invalidateQueries(QUERY_KEYS.list);
        qc.invalidateQueries(QUERY_KEYS.show(uid));
      },
    },
  );
};

/** PATCH /toggle — optimistic on the list. */
export const useToggleShippingRule = () => {
  const qc = useQueryClient();
  return useSentryMutation<{ object: { is_active: boolean } }, ErrorMessageResponse, string>(
    (uid: string) => toggleShippingRule(uid),
    {
      onMutate: async (uid: string) => {
        await qc.cancelQueries(QUERY_KEYS.list);
        const prev = qc.getQueryData<ShippingRulesListResponse>(QUERY_KEYS.list);
        if (prev?.object) {
          qc.setQueryData<ShippingRulesListResponse>(QUERY_KEYS.list, {
            ...prev,
            object: prev.object.map((r: ShippingRule) =>
              r.uid === uid ? { ...r, is_active: !r.is_active } : r,
            ),
          });
        }
        return { prev };
      },
      onError: (_err, _uid, context: any) => {
        if (context?.prev) qc.setQueryData(QUERY_KEYS.list, context.prev);
      },
      onSettled: () => qc.invalidateQueries(QUERY_KEYS.list),
    },
  );
};

/** RS-16 — pre-creation rule evaluator. Used by add-new-order flow. */
export const useEvaluateShippingRules = () =>
  useSentryMutation<EvaluateShippingRulesResponse, ErrorMessageResponse, EvaluateShippingRulesRequest>(
    (payload) => evaluateShippingRules(payload),
  );

/** DELETE — soft-delete server-side. */
export const useDeleteShippingRule = () => {
  const qc = useQueryClient();
  return useSentryMutation<{ object: { uid: string } }, ErrorMessageResponse, string>(
    (uid: string) => deleteShippingRule(uid),
    {
      onSuccess: () => {
        qc.invalidateQueries(QUERY_KEYS.list);
      },
    },
  );
};
